import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { fmtLocalDate, fmtLocalShort } from '../../utils/datetime';
import { qcBatchApi, type QcAvailableReagent, type QcInstrument, type QcLinkedReagent } from './qcBatch.api';
import { useMiniGrid, type MiniCol } from './useMiniGrid';

// ── Others tab (Reagent / Instrument) of the QC Batch and Prep Batch forms ─────
// One copy for both forms. The grids get Hide / Show All / Column Chooser (header
// right-click), click-to-sort, the Excel-style filter and resize through the shared
// useMiniGrid (user 2026-09-29). Selection stays by key, so a sort/filter never
// changes WHICH rows Remove acts on — only what is shown.

/** Linked (saved batch) and staged (unsaved batch) reagents in one row shape. */
export interface OtherReagentRow {
  key: string; reagentID: string | null; reagentName: string | null;
  expiredDate: string | null; datePrepared: string | null; preparedBy: string | null;
  hasCoa: boolean; dateReceived: string | null; vendorName: string | null;
}

interface SelectionProps {
  isSelected: (key: string) => boolean;
  toggle: (key: string) => void;
  allChecked: boolean;
  toggleAll: (on: boolean) => void;
}

const leadFor = <T,>(keyOf: (r: T) => string, sel: SelectionProps) => ({
  width: 30,
  head: <input type="checkbox" className="chk" checked={sel.allChecked} onChange={e => sel.toggleAll(e.target.checked)} />,
  cell: (r: T) => (
    <input type="checkbox" className="chk" readOnly checked={sel.isSelected(keyOf(r))}
      onClick={e => e.stopPropagation()} onChange={() => sel.toggle(keyOf(r))} />
  ),
});

/** Reagent grid of the Others tab. `saved` picks the linked rows (else the staged ones). */
export function useOthersReagentGrid(p: SelectionProps & {
  saved: boolean; linked: QcLinkedReagent[]; staged: QcAvailableReagent[];
  onCoa: (reagentID: string | null) => void;
}) {
  const { saved, linked, staged, onCoa } = p;
  // Staged (pre-save) reagents come from the Add Reagents catalog, which carries no COA
  // flag — look it up so the COA link shows before the batch is saved (user report
  // 2026-09-29; it used to appear only after save). A failed lookup just leaves the
  // links off; the grid itself is unaffected.
  const [stagedCoa, setStagedCoa] = useState<Record<string, boolean>>({});
  const stagedIdsKey = saved ? '' : staged.map(r => r.reagentID ?? '').filter(Boolean).join(',');
  useEffect(() => {
    if (!stagedIdsKey) return;
    let cancelled = false;
    qcBatchApi.reagentCoaFlags(stagedIdsKey.split(','))
      .then(flags => {
        if (cancelled) return;
        setStagedCoa(prev => {
          const next = { ...prev };
          for (const f of flags) next[f.reagentID] = f.hasCoa;
          return next;
        });
      })
      .catch(() => { /* COA link stays hidden; the API logs the failure (rule 13). */ });
    return () => { cancelled = true; };
  }, [stagedIdsKey]);
  const rows = useMemo<OtherReagentRow[]>(() => saved
    ? linked.map(r => ({
        key: r.oid, reagentID: r.reagentID, reagentName: r.reagentName, expiredDate: r.expiredDate,
        datePrepared: r.datePrepared, preparedBy: r.preparedBy, hasCoa: r.hasCoa,
        dateReceived: r.dateReceived, vendorName: r.vendorName }))
    : staged.map(r => ({
        key: r.reagentID ?? '', reagentID: r.reagentID, reagentName: r.reagentName, expiredDate: r.expiryDate,
        datePrepared: r.datePrepared, preparedBy: r.preparedBy, hasCoa: !!(r.reagentID && stagedCoa[r.reagentID]),
        dateReceived: r.dateReceived, vendorName: r.vendorName ?? null })),
  [saved, linked, staged, stagedCoa]);
  const cols = useMemo<MiniCol<OtherReagentRow>[]>(() => [
    { key: 'reagentID',    label: 'ReagentID',    width: 110, text: r => r.reagentID || '' },
    { key: 'reagentName',  label: 'ReagentName',  width: 170, text: r => r.reagentName || '' },
    { key: 'expiredDate',  label: 'ExpiredDate',  text: r => fmtLocalDate(r.expiredDate), sortValue: r => r.expiredDate },
    { key: 'datePrepared', label: 'DatePrepared', width: 130, text: r => fmtLocalShort(r.datePrepared), sortValue: r => r.datePrepared },
    { key: 'preparedBy',   label: 'PreparedBy',   text: r => r.preparedBy || '' },
    // COA link only when the reagent has a certificate on file (server-computed hasCoa) —
    // legacy hides the COA button otherwise (Grid_HtmlCommandCellPrepared :4958).
    { key: 'coa',          label: 'COA',          text: r => (r.hasCoa ? 'COA' : ''),
      cell: r => r.hasCoa && (
        <button className="coa-link" onClick={e => { e.stopPropagation(); onCoa(r.reagentID); }}>COA</button>) },
    { key: 'dateReceived', label: 'DateReceived', width: 130, text: r => fmtLocalShort(r.dateReceived), sortValue: r => r.dateReceived },
    { key: 'vendorName',   label: 'Vendor',       width: 140, text: r => r.vendorName || '' },
  ], [onCoa]);
  return useMiniGrid(cols, rows, {
    rowKey: r => r.key, empty: 'No reagents added.',
    lead: leadFor<OtherReagentRow>(r => r.key, p),
    rowProps: r => ({ onClick: () => p.toggle(r.key) }),
  });
}

const INSTRUMENT_COLS: MiniCol<QcInstrument>[] = [
  { key: 'labwareName',          label: 'InstrumentNumber',     width: 130, text: r => r.labwareName || '' },
  { key: 'instrumentName',       label: 'InstrumentName',       width: 180, text: r => r.instrumentName || '' },
  { key: 'manufacturer',         label: 'Manufacturer',         text: r => r.manufacturer || '' },
  { key: 'expirationDate',       label: 'ExpirationDate',       text: r => fmtLocalDate(r.expirationDate), sortValue: r => r.expirationDate },
  { key: 'departmentUsedName',   label: 'DepartmentUsed',       text: r => r.departmentUsedName || '' },
  { key: 'active',               label: 'Active',               text: r => (r.active === undefined ? '' : r.active ? 'Yes' : 'No') },
  { key: 'maintenanceFrequency', label: 'MaintenanceFrequency', text: r => r.maintenanceFrequency || '' },
  { key: 'departmentNames',      label: 'Department',           text: r => r.departmentNames || '' },
  { key: 'categoryName',         label: 'Category',             text: r => r.categoryName || '' },
  { key: 'retiredDate',          label: 'RetiredDate',          text: r => fmtLocalDate(r.retiredDate), sortValue: r => r.retiredDate },
];

/** Instrument grid of the Others tab (`rows` = the linked or the staged instruments). */
export function useOthersInstrumentGrid(p: SelectionProps & { rows: QcInstrument[] }) {
  return useMiniGrid(INSTRUMENT_COLS, p.rows, {
    rowKey: r => r.instrumentOid, empty: 'No instruments added.',
    lead: leadFor<QcInstrument>(r => r.instrumentOid, p),
    rowProps: r => ({ onClick: () => p.toggle(r.instrumentOid) }),
  });
}

/** Others-tab section: title · Add/Remove toolbar · grid · footer (was a copy in each form). */
export function OtherSection({ title, onAdd, onRemove, count, grid, disabled }:
  { title: string; onAdd: () => void; onRemove: () => void; count: number; grid: ReactNode; disabled?: boolean }) {
  return (
    <div className="osect">
      <h3>{title}</h3>
      <div className="subtb">
        <button onClick={onAdd} disabled={disabled}><span className="scirc g">＋</span> Add</button>
        <button onClick={onRemove} disabled={disabled}><span className="scirc r">−</span> Remove</button>
      </div>
      <div className="gridbox">{grid}</div>
      <div className="foot"><span>Page 1 of 1 ({count} items)</span>
        <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
        <div className="psize">Page size: <select defaultValue="20" data-unsaved-ignore><option>20</option></select></div></div>
    </div>
  );
}
